import { CompanyEppConfigI } from '../api/companies/interfaces/CompanyEppConfig';
import { EmployerContributionTypeEnum } from '../types/enums';
import { resolveMatchingContribution } from './resolveMatchingContribution';

/**
 * Resolve employer contribution per-employee (monthly €).
 *
 * Replicates V1 priority chain from:
 * - VoluntaryExitModal.tsx L149-200 (full resolution)
 * - EditContributionModal.tsx L560-595 (seniorityBracket tiers)
 *
 * Priority:
 * 0. matchingBracket (MATCHING mode — employer matches the employee contribution):
 *    live preview if employeeMonthlyContribution is provided (slider), otherwise the
 *    backend snapshot (calculatedEmployerContribution). Never falls through to other modes.
 * 1. calculatedEmployerContribution (backend pre-calculated)
 * 2. categoryBracket[employeeCategory] (category-based)
 * 3. seniorityBracket tiers (by years of service — type-independent)
 * 4. GROSS_SALARY_PERCENTAGE uniform (employerContributionAmount as %)
 * 5. FIXED_AMOUNT (employerContributionAmount as €)
 */
export function resolveEmployerContribution(params: {
	calculatedEmployerContribution?: number;
	companyEppConfig?: CompanyEppConfigI | null;
	employeeCategory?: string;
	grossAnnualSalary?: number;
	employmentStartDate?: string;
	/**
	 * Live employee contribution in €/month (e.g. slider value). Only used in
	 * MATCHING mode to recalculate the employer preview in real time.
	 */
	employeeMonthlyContribution?: number;
}): number {
	const {
		calculatedEmployerContribution,
		companyEppConfig,
		employeeCategory,
		grossAnnualSalary,
		employmentStartDate,
		employeeMonthlyContribution
	} = params;

	// 0. MATCHING mode (exclusive: never falls through to other modes)
	if (companyEppConfig?.matchingBracket) {
		// Live preview takes precedence over the backend snapshot: the snapshot
		// reflects the SAVED contribution, not the slider the user is moving
		if (employeeMonthlyContribution !== undefined) {
			return resolveMatchingContribution({
				matchingBracket: companyEppConfig.matchingBracket,
				employeeCategory,
				employeeMonthlyContribution
			});
		}
		if (calculatedEmployerContribution !== undefined && calculatedEmployerContribution !== null) {
			return Number(calculatedEmployerContribution);
		}
		// Without live amount nor snapshot we cannot know the matching → 0
		// (uniform fallbacks like employerContributionAmount do NOT apply here)
		return 0;
	}

	// 1. Backend pre-calculated (highest priority)
	if (calculatedEmployerContribution !== undefined && calculatedEmployerContribution !== null) {
		return Number(calculatedEmployerContribution);
	}

	// 2. Category bracket (non-uniform, category-based fixed amounts)
	if (companyEppConfig?.categoryBracket && employeeCategory) {
		const cat =
			companyEppConfig.categoryBracket[
				employeeCategory as keyof typeof companyEppConfig.categoryBracket
			];
		if (cat && typeof cat.contributionAmount === 'number') {
			return cat.periodicity === 'YEARLY' ? cat.contributionAmount / 12 : cat.contributionAmount;
		}
	}

	// 3. Seniority bracket (type-independent — works for FIXED_AMOUNT, GROSS_SALARY_PERCENTAGE, etc.)
	if (companyEppConfig?.seniorityBracket?.tiers?.length) {
		const currentDate = new Date();
		const startDate = employmentStartDate ? new Date(employmentStartDate) : null;
		const yearsOfService = startDate
			? currentDate.getFullYear() -
				startDate.getFullYear() +
				(currentDate.getMonth() - startDate.getMonth()) / 12
			: 0;

		const seniorityBracket = companyEppConfig.seniorityBracket;

		for (const tier of seniorityBracket.tiers) {
			const minYears = tier.minYears || 0;
			const maxYears = tier.maxYears;

			if (yearsOfService >= minYears && (maxYears === null || yearsOfService < maxYears)) {
				// Fixed amount tier
				if (
					tier.employerContributionAmount !== undefined &&
					tier.employerContributionAmount !== null
				) {
					const amount = tier.employerContributionAmount;
					if (tier.periodicity === 'YEARLY') return amount / 12;
					if (tier.periodicity === 'QUARTERLY') return amount / 3;
					if (tier.periodicity === 'SEMI_ANNUALLY') return amount / 6;
					if (tier.periodicity === 'BI_MONTHLY') return amount / 2;
					return amount; // MONTHLY or default
				}
				// Percentage tier
				if (tier.employerContributionPercentage !== undefined) {
					return Number((grossAnnualSalary ?? 0) / 100 / 12) * tier.employerContributionPercentage;
				}
				break;
			}
		}

		// Fallback: use minEmployeeContributionPercentage as default percentage
		const fallbackPct = seniorityBracket?.minEmployeeContributionPercentage || 1;
		return Number((grossAnnualSalary ?? 0) / 100 / 12) * fallbackPct;
	}

	// 4. GROSS_SALARY_PERCENTAGE uniform
	if (
		companyEppConfig?.employerContributionType ===
		EmployerContributionTypeEnum.GROSS_SALARY_PERCENTAGE
	) {
		return (
			(Number(grossAnnualSalary ?? 0) * Number(companyEppConfig.employerContributionAmount || 0)) /
			12 /
			100
		);
	}

	// TODO B1: SALARY_BRACKET con periodicity YEARLY — backend devuelve monto anual sin convertir.
	// Afecta: Lemara Restauraciones (YEARLY), Madrid Sur Dental (YEARLY), MOBA Trading (YEARLY).
	// Requiere fix en comparsa o workaround frontend. Ref: testing marzo 2026.

	// 5. FIXED_AMOUNT (uniform) — VoluntaryExitModal.tsx L150-152
	return Number(companyEppConfig?.employerContributionAmount || 0);
}
